import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Checkbox from "@mui/material/Checkbox";
import React from "react";
import styled from "styled-components";
import { Button, TextView } from "../../../components";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";
import { useDispatch } from "react-redux";
import { openUpgradePopup } from "../../../redux/reducers/upgradePopup";

const TooltipContainer = styled.div`
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: ${(props) => (props.isWelcome ? "24px" : "15px")};
  max-width: ${(props) => (props.isWelcome ? "400px" : "330px")};
  position: relative;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  text-align: center;
`;

const StyledButton = styled(Button)`
  width: 140px;
`;

const DotContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 15px 0;
  align-items: center;
`;

const Dot = styled.div`
  width: ${(props) => (props.selected ? "12px" : "8px")};
  height: ${(props) => (props.selected ? "12px" : "8px")};
  border-radius: 50%;
  background-color: ${(props) =>
    props.selected ? colors.PrimaryPurple : "#E0E0E0"};
`;

const ButtonContainer = styled.div`
  display: grid;
  justify-content: flex-end;
  grid-template-columns: ${(props) =>
    props.isWelcome ? "120px" : props.isLastStep ? "80px" : "70px 80px"};
`;

const BackButtonContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 10px;
  color: ${colors.PrimaryPurple};
`;

const GalleryTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
  doNotShowAgain = false,
  onDoNotShowAgainChange = () => {},
}) => {
  const isLastStep = index === size - 1;
  const dispatch = useDispatch();

  React.useEffect(() => {
    // Attach click handlers when the business box or feedback step is active
    if (
      step.target === ".business-box" ||
      step.target === ".feedback-button" ||
      step.target === ".profile-menu"
    ) {
      const premiumLink = document.getElementById("premium-link");
      const prosuiteLink = document.getElementById("prosuite-link");
      const upgradingLink = document.getElementById("upgrading-link");
      const premiumEarningsLink = document.getElementById(
        "premium-earnings-link"
      );
      const prosuiteEarningsLink = document.getElementById(
        "prosuite-earnings-link"
      );

      const openSubscriptionModal = (requiredSubscriptionValue) => {
        dispatch(
          openUpgradePopup({
            requiredSubscription: requiredSubscriptionValue,
          })
        );
      };

      if (premiumLink && step.target === ".business-box") {
        premiumLink.onclick = () => openSubscriptionModal(2);
      }
      if (prosuiteLink && step.target === ".business-box") {
        prosuiteLink.onclick = () => openSubscriptionModal(2);
      }
      if (upgradingLink && step.target === ".feedback-button") {
        upgradingLink.onclick = () => openSubscriptionModal(1);
      }
      if (premiumEarningsLink && step.target === ".profile-menu") {
        premiumEarningsLink.onclick = () => openSubscriptionModal(2);
      }
      if (prosuiteEarningsLink && step.target === ".profile-menu") {
        prosuiteEarningsLink.onclick = () => openSubscriptionModal(2);
      }

      // Clean up event listeners when the step changes
      return () => {
        if (premiumLink) premiumLink.onclick = null;
        if (prosuiteLink) prosuiteLink.onclick = null;
        if (upgradingLink) upgradingLink.onclick = null;
        if (premiumEarningsLink) premiumEarningsLink.onclick = null;
        if (prosuiteEarningsLink) prosuiteEarningsLink.onclick = null;
      };
    }
  }, [step, dispatch]);

  return (
    <TooltipContainer {...tooltipProps} isWelcome={step.isWelcome}>
      {step.isWelcome ? (
        <>
          <CloseButton {...skipProps}>
            <CloseIcon fontSize="small" />
          </CloseButton>
          <WelcomeContent>
            <img src={ASSETS.DialogImg} alt="Welcome" />
            <TextView variant="subtitle" style={{ fontWeight: 700 }}>
              Welcome to your Gallery
            </TextView>
            <TextView variant="largeText">
              This is where all your Wheels live, and where guests can explore
              your public content.
            </TextView>
            <StyledButton {...primaryProps} variant="outlined">
              Explore Now
            </StyledButton>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "8px",
              }}
            >
              <Checkbox
                size="small"
                checked={doNotShowAgain}
                onChange={(e) => onDoNotShowAgainChange(e.target.checked)}
              />
              <TextView>Do not show again</TextView>
            </div>
          </WelcomeContent>
        </>
      ) : (
        <>
          <TextView
            variant="subtitle"
            style={{ fontWeight: 700, marginBottom: 10 }}
          >
            {step.heading}
          </TextView>
          <TextView dangerouslySetInnerHTML={{ __html: step.content }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {index > 1 && (
                <BackButtonContainer {...backProps}>
                  <ChevronLeftIcon style={{ fontSize: 22 }} />
                </BackButtonContainer>
              )}
              <DotContainer>
                {Array.from({ length: size - 1 }).map((_, i) => (
                  <Dot key={i} selected={i === index - 1} />
                ))}
              </DotContainer>
            </div>
            <ButtonContainer isLastStep={isLastStep} isWelcome={step.isWelcome}>
              {!step.isWelcome && !isLastStep && (
                <Button variant="secondary" {...skipProps}>
                  Skip
                </Button>
              )}
              {isLastStep ? (
                <Button {...closeProps}>Close</Button>
              ) : (
                <Button {...primaryProps}>Next</Button>
              )}
            </ButtonContainer>
          </div>
        </>
      )}
    </TooltipContainer>
  );
};

export default GalleryTooltip;
